import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import cacheService from '../services/cacheService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillTransfer, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const RatesContainer = styled.div`
  position: relative;
  cursor: pointer;
  color: white;
`;

const ExchangeIcon = styled.div`
  font-size: 1.2rem;
  padding: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: #F2C143;
  }
`;

const RatesPopup = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem;
  min-width: 200px;
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const RateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  color: #333;
  
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const CurrencyFlag = styled.img`
  width: 24px;
  height: 16px;
  margin-right: 8px;
`;

const CurrencyLabel = styled.div`
  display: flex;
  align-items: center;
`;

const RateValue = styled.span`
  font-weight: 500;
`;

const PopupTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const TitleText = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
`;

const InfoTooltip = styled.div`
  position: relative;
  cursor: help;
  color: #666;
  
  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipText = styled.div`
  position: absolute;
  bottom: 125%;
  right: -80px;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  font-size: 0.85rem;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1001;
  width: 280px;
  text-align: center;
  line-height: 1.4;
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const ExchangeRate = ({ onPopupToggle, isOpen }) => {
  const [rates, setRates] = useState(null);
  const containerRef = useRef(null);
  const { language } = useLanguage();
  
  // Exchange rate translations
  const exchangeTranslations = {
    en: {
      title: 'Exchange Rate',
      tooltip: 'These are average values. Exchange rates may vary depending on the location and service provider.'
    },
    es: {
      title: 'Tipo de Cambio',
      tooltip: 'Estos son valores promedio. Los tipos de cambio pueden variar dependiendo de la ubicación y el proveedor de servicios.'
    }
  };
  
  const t = exchangeTranslations[language] || exchangeTranslations.en;

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const data = await cacheService.getExchangeRates();
        setRates(data);
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
      }
    };

    fetchRates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onPopupToggle();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onPopupToggle]);

  if (!rates) return null;

  return (
    <RatesContainer ref={containerRef}>
      <ExchangeIcon onClick={onPopupToggle}>
        <FontAwesomeIcon icon={faMoneyBillTransfer} />
      </ExchangeIcon>
      <RatesPopup show={isOpen}>
        <PopupTitle>
          <TitleText>{t.title}</TitleText>
          <InfoTooltip>
            <FontAwesomeIcon icon={faInfoCircle} />
            <TooltipText className="tooltip-text">
              {t.tooltip}
            </TooltipText>
          </InfoTooltip>
        </PopupTitle>
        <RateItem>
          <CurrencyLabel>
            <CurrencyFlag 
              src="https://flagcdn.com/w40/us.png" 
              alt="USD Flag"
            />
            USD
          </CurrencyLabel>
          <RateValue>RD$ {rates.usd_rate}</RateValue>
        </RateItem>
        <RateItem>
          <CurrencyLabel>
            <CurrencyFlag 
              src="https://flagcdn.com/w40/eu.png" 
              alt="EUR Flag"
            />
            EUR
          </CurrencyLabel>
          <RateValue>RD$ {rates.eur_rate}</RateValue>
        </RateItem>
      </RatesPopup>
    </RatesContainer>
  );
};

export default ExchangeRate;
